import { useState, useContext, useEffect, useRef } from 'react';
import { useGet } from '@/query/fetchHooks';
import { useParams, useNavigate } from 'react-router-dom';
import { NavContext } from '@/components/NavContext';
import { AuthContext } from '@/components/Contexts/AuthContext';
import { Userwithorganisationwithidps } from '@/models';
import ScormLessonHeader from './ScormLessonHeader';
import ScormLessonContent from './ScormLessonContent';
import { useMediaQuery } from 'react-responsive';
import { ScormService } from './ScormService';
import { mongoEncodeKey, useStateAndRef, nthLastIndexOf } from '@/util';
import { config } from '@/config';
import { axios } from '@/axios';
import { SidebarScormStudent } from '@/components/LessonTree/SidebarScormStudent';
import { useTranslation } from 'react-i18next';
import { Sidebar, Cross } from '@teo/components/icons';
import { IconButton } from '@teo/components';
import { getScormSessionTimeDelta, parseScormTimeToSeconds } from './scormTime';

type ScormTimeTracker = {
  coursecontentId: number | null;
  courseId: string;
  flushPromise: Promise<void> | null;
  lastSessionSeconds: number | undefined;
  lessonId: number;
  pendingSeconds: number;
  userId: string;
  version: string | null;
};

const ScormLessonPage = ({ courseInfo }: any) => {
  const iFrameRef = useRef<any>(null);

  const { setNavDisabled } = useContext(NavContext);

  // useStateAndRef is NOT generic in your codebase
  const [activeNode, realSetActiveNode, activeNodeRef] = useStateAndRef(
    null as any
  );

  const [hideSidebar, setHideSidebar] = useState<boolean>(false);
  const [numLesAs, setNumLesAs] = useState<number>(1);
  const [initialised, setInitialised] = useState<boolean>(false);

  const navigate = useNavigate();
  const { t } = useTranslation();

  const isDesctop = useMediaQuery({ query: '(max-width: 1023px)' });
  const isMobile = useMediaQuery({ query: '(max-width: 767px)' });
  const iSsmallLaptop = useMediaQuery({ query: '(max-width: 1199px)' });

  useEffect(() => {
    setNavDisabled(true);
    return () => setNavDisabled(false);
  }, []);

  const [navigateTo, setNavigateTo] = useState<null | string>(null);
  useEffect(() => {
    if (navigateTo) navigate(navigateTo);
  }, [navigateTo]);

  // stable refs
  const subscriptionsRef = useRef<any[]>([]);
  const lastWidgetIdRef = useRef<any>(0);
  const scormWidgetsRef = useRef<Record<string, any>>({});

  const hideScroll = () => (document.body.style.overflow = 'hidden');
  const showScroll = () => (document.body.style.overflow = 'auto');

  const parseInteraction = (path: string, data: any) => {
    const p = path.split('.').slice(3).join('.');
    const widgets = scormWidgetsRef.current;

    switch (p) {
      case 'id': {
        const id = mongoEncodeKey(data[0]);
        lastWidgetIdRef.current = id;
        widgets[id] = { id };
        break;
      }
      case 'type':
        widgets[lastWidgetIdRef.current].type = data[0];
        break;
      case 'learner_response':
        widgets[lastWidgetIdRef.current].learner_response = data[0];
        break;
      case 'result':
        widgets[lastWidgetIdRef.current].result = data[0];
        break;
      case 'correct_responses.0.pattern':
        widgets[lastWidgetIdRef.current].correct_responses = data[0];
        break;
      case 'description':
        widgets[lastWidgetIdRef.current].description = data[0];
        break;
      case 'weighting':
        widgets[lastWidgetIdRef.current].weighting = parseFloat(data[0]);
        break;
      case 'latency':
        widgets[lastWidgetIdRef.current].latency = data[0];
        break;
      case 'timestamp':
        widgets[lastWidgetIdRef.current].timestamp = data[0];
        break;
      default:
        console.error("I can't deal with", p, data);
    }
  };

  const { auth } = useContext(AuthContext);
  const authUser = auth?.user as Userwithorganisationwithidps;

  const { courseId } = useParams();
  let { studentId } = useParams();

  let itSMe = false;
  if (!studentId) {
    itSMe = true;
    studentId = authUser?.id as unknown as string;
  }
  const isTeacher = !itSMe;

  const userId = studentId;
  const isPreview =
    window.location.pathname.includes('/preview/') || !studentId;

  const [student, setstudent, studentRef] = useStateAndRef(null as any);
  const [course, setCourse] = useState<any>(null);

  const basePath = window.location.pathname.slice(
    0,
    nthLastIndexOf(window.location.pathname, '/', 2)
  );
  let typePath = course?.courseType + 's';
  if (typePath === 'leervacatures') typePath = 'vacancies';
  const returnPath = `${basePath}/${typePath}/${course?.id}`;

  // ------------------------------------------------------------
  // Refetch trigger (header + sidebar)
  // ------------------------------------------------------------
  const [refetchNonce, setRefetchNonce] = useState<number>(0);
  const refetchTimerRef = useRef<any>(null);
  const refetchInFlightRef = useRef<boolean>(false);

  const refetchStudent = async () => {
    if (!courseId || !userId) return;
    const res = await axios.get(
      `${config.backend}/student/${courseId}/${userId}`
    );
    const data = res?.data || {};
    setstudent(data || {});
    Object.assign(studentRef.current || {}, data || {});
  };

  const queueRefetch = (reason: string) => {
    if (isPreview || isTeacher) return;
    if (!courseId || !userId) return;

    if (refetchTimerRef.current) clearTimeout(refetchTimerRef.current);

    // debounce bursts of SetValue/PUTs
    refetchTimerRef.current = setTimeout(async () => {
      if (refetchInFlightRef.current) return;
      refetchInFlightRef.current = true;

      try {
        await refetchStudent(); // header progress (student)
        setRefetchNonce((x) => x + 1); // sidebar statuses trigger
      } finally {
        refetchInFlightRef.current = false;
      }
    }, 600);
  };

  // ------------------------------------------------------------
  // Data fetches
  // ------------------------------------------------------------
  useGet(`/student/${courseId}/${userId}`, {
    enabled: !!userId,
    onSuccess: (result: any) => setstudent(result?.data || {}),
  });

  useGet(`/coursedetail/${courseId}`, {
    onSuccess: (result: any) => setCourse(result?.data || {}),
  });

  useEffect(() => {
    if (isMobile && course) setHideSidebar(true);
    else setHideSidebar(false);
  }, [isMobile, course]);

  // ------------------------------------------------------------
  // lessonstatuses: load all on start
  // ------------------------------------------------------------
  const [statuses, setStatuses, statusesRef] = useStateAndRef(
    null as any[] | null
  );

  useGet(`/lessonstatuses`, {
    courseId: courseId,
    userId: userId,
    enabled: !!courseId && !!userId && !isPreview,
    onSuccess: (result: any) => setStatuses(result?.data || []),
  });

  const upsertStatusInState = (row: any) => {
    const current = statusesRef.current ? [...statusesRef.current] : [];
    const idx = current.findIndex((x: any) => x?.lessonId === row?.lessonId);
    if (idx !== -1) current[idx] = row;
    else current.push(row);
    setStatuses(current);
  };

  // ------------------------------------------------------------
  // follow update — ONLY for startDate now
  // ------------------------------------------------------------
  const updateFollow = async (data: any) => {
    setstudent((prev: any) => Object.assign({}, prev, data));
    Object.assign(studentRef.current, data);
    await axios.put(`${config.backend}/follow/${courseId}/${userId}`, data);
  };

  const startDateSetRef = useRef<boolean>(false);

  // ✅ keep follow.progressLessonsTotal in sync with manifest SCO count
  const lastProgressTotalSentRef = useRef<number | null>(null);

  useEffect(() => {
    if (isPreview || isTeacher) return;
    if (!courseId || !userId) return;

    // wait until manifest is parsed (SidebarScormStudent sets numLesAs)
    if (!numLesAs || numLesAs <= 0) return;

    const curTotalRaw = studentRef.current?.progressLessonsTotal;
    const curTotal =
      typeof curTotalRaw === 'number'
        ? curTotalRaw
        : parseInt(curTotalRaw || '0', 10);

    if (curTotal === numLesAs) return;

    // avoid repeating the same PUT
    if (lastProgressTotalSentRef.current === numLesAs) return;
    lastProgressTotalSentRef.current = numLesAs;

    updateFollow({ progressLessonsTotal: numLesAs }).catch(() => {
      // allow retry later if PUT failed
      lastProgressTotalSentRef.current = null;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numLesAs, courseId, userId, isPreview, isTeacher]);

  // ------------------------------------------------------------
  // SCO => Lesson mapping
  // ------------------------------------------------------------
  const itemId: string | null = activeNode?.['@_identifier'] || null;

  const buildSourceKey = () => {
    if (
      !(course?.masterId || course?.coursecontentId) ||
      !course?.publicationPath ||
      !itemId
    )
      return null;
    return `${course.masterId || course.coursecontentId}:${
      course.publicationPath
    }:${itemId}`;
  };

  // Cache lessons + dedupe in-flight
  const lessonCacheRef = useRef<Record<string, any>>({});
  const lessonPromiseRef = useRef<Record<string, Promise<any> | null>>({});

  // Active SCO lesson + status local cache
  const activeLessonRef = useRef<any>(null);
  const activeStatusRef = useRef<any>(null);
  const scormTimeTrackersRef = useRef<Record<number, ScormTimeTracker>>({});

  const getOrCreateLessonForSCO = async () => {
    if (isPreview || isTeacher) return null;
    const sourceKey = buildSourceKey();

    if (!sourceKey) return null;

    const coursecontentId = course?.coursecontentId || course?.masterId || null;
    if (!coursecontentId) return null;

    if (lessonCacheRef.current[sourceKey])
      return lessonCacheRef.current[sourceKey];
    if (lessonPromiseRef.current[sourceKey])
      return lessonPromiseRef.current[sourceKey];

    const name = activeNode?.title || null;

    const p = axios
      .post(
        `${config.backend}/lesson/upsert/${encodeURIComponent(sourceKey)}`,
        {
          name,
          sourceType: 'scorm',
          lessonType: 'scorm',
          coursecontentId,
          onlyTheory: false,
        }
      )
      .then((res) => {
        const lesson = res?.data || null;
        if (lesson) lessonCacheRef.current[sourceKey] = lesson;
        return lesson;
      })
      .finally(() => {
        lessonPromiseRef.current[sourceKey] = null;
      });

    lessonPromiseRef.current[sourceKey] = p;
    return p;
  };

  const findStatusRowByLessonId = (lessonId: number) => {
    return (
      statusesRef.current?.find((x: any) => x?.lessonId === lessonId) || null
    );
  };

  // ------------------------------------------------------------
  // Ensure active lesson + active status exist on node change
  // ------------------------------------------------------------
  const ensureActiveLessonAndStatus = async () => {
    if (isPreview || isTeacher) return;
    if (!courseId || !userId) return;

    const lesson = await getOrCreateLessonForSCO();
    activeLessonRef.current = lesson;

    const lessonId = lesson?.id;
    if (!lessonId) return;

    if (activeStatusRef.current?.lessonId === lessonId) return;

    const existing = findStatusRowByLessonId(lessonId);
    if (existing) {
      activeStatusRef.current = existing;
      return;
    }

    const res = await axios.post(`${config.backend}/lessonstatuses`, {
      status: 'started',
      version: activeNode?.version || activeNode?.['@_identifierref'] || itemId,
      coursecontentId: course?.coursecontentId || course?.masterId,
      courseId,
      userId,
      lessonId,
    });

    if (res?.data) {
      upsertStatusInState(res.data);
      activeStatusRef.current = res.data;
      queueRefetch('created started lessonstatus');
    }
  };

  useEffect(() => {
    if (!initialised) return;
    if (!activeNode?.['@_identifier']) return;
    if (!courseId || !userId) return;
    ensureActiveLessonAndStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialised, activeNode?.['@_identifier'], statuses]);

  // ------------------------------------------------------------
  // Diff-based lessonstatus update
  // ------------------------------------------------------------
  const nearlyEqual = (a: any, b: any) => {
    if (typeof a === 'number' && typeof b === 'number') {
      return Math.abs(a - b) < 0.00001;
    }
    return a === b;
  };

  const buildChangedPatch = (current: any, patch: any) => {
    const out: any = {};
    for (const k of Object.keys(patch || {})) {
      const nextVal = patch[k];
      const curVal = current ? current[k] : undefined;
      if (!nearlyEqual(curVal, nextVal)) out[k] = nextVal;
    }
    return out;
  };

  const upsertLessonstatus = async (patch: any) => {
    if (isPreview || isTeacher) return null;
    if (!courseId || !userId) return null;

    await ensureActiveLessonAndStatus();

    const lesson = activeLessonRef.current;
    const lessonId = lesson?.id;
    if (!lessonId) return null;

    const current =
      activeStatusRef.current || findStatusRowByLessonId(lessonId);
    if (!current) return null;

    const changed = buildChangedPatch(current, patch);
    if (!Object.keys(changed).length) return current;

    const res = await axios.put(
      `${config.backend}/lessonstatus/${courseId}/${userId}/${lessonId}`,
      {
        courseId: current.courseId || courseId,
        userId: current.userId || userId,
        lessonId,
        ...changed,
      }
    );

    if (res?.data) {
      upsertStatusInState(res.data);
      activeStatusRef.current = res.data;
      queueRefetch('lessonstatus updated');
    }

    return res?.data || null;
  };

  const flushScormTime = (lessonId: number) => {
    const tracker = scormTimeTrackersRef.current[lessonId];
    if (!tracker) return Promise.resolve();
    if (tracker.flushPromise) return tracker.flushPromise;

    const flushPromise = (async () => {
      while (tracker.pendingSeconds > 0) {
        const seconds = tracker.pendingSeconds;

        try {
          await axios.post(`${config.backend}/follows/timetick`, {
            courseId: tracker.courseId,
            coursecontentId: tracker.coursecontentId,
            lessonId: tracker.lessonId,
            seconds,
            userId: tracker.userId,
            version: tracker.version,
          });

          tracker.pendingSeconds -= seconds;

          if (activeStatusRef.current?.lessonId === lessonId) {
            activeStatusRef.current = {
              ...activeStatusRef.current,
              timeSpent:
                Number(activeStatusRef.current?.timeSpent || 0) + seconds,
            };
          }

          queueRefetch('SCORM session time updated');
        } catch (error) {
          // Keep pendingSeconds intact so the next SCORM time event retries it.
          console.error('Unable to persist SCORM session time', error);
          break;
        }
      }
    })();

    tracker.flushPromise = flushPromise;
    flushPromise.finally(() => {
      if (tracker.flushPromise === flushPromise) tracker.flushPromise = null;
    });

    return flushPromise;
  };

  const persistScormSessionTime = async (value: unknown) => {
    const parsedSeconds = parseScormTimeToSeconds(value);
    if (parsedSeconds === null) {
      console.warn('Ignoring invalid SCORM session time', value);
      return;
    }

    await ensureActiveLessonAndStatus();

    const lesson = activeLessonRef.current;
    const status = activeStatusRef.current;
    const lessonId = Number(lesson?.id);
    if (!lessonId || !status) return;

    let tracker = scormTimeTrackersRef.current[lessonId];
    if (!tracker) {
      tracker = {
        coursecontentId: course?.coursecontentId || course?.masterId || null,
        courseId: String(courseId),
        flushPromise: null,
        lastSessionSeconds: undefined,
        lessonId,
        pendingSeconds: 0,
        userId: String(userId),
        version:
          activeNode?.version || activeNode?.['@_identifierref'] || itemId,
      };
      scormTimeTrackersRef.current[lessonId] = tracker;
    }

    const delta = getScormSessionTimeDelta(
      tracker.lastSessionSeconds,
      parsedSeconds
    );
    tracker.lastSessionSeconds = parsedSeconds;
    tracker.pendingSeconds += delta;

    if (tracker.pendingSeconds > 0) await flushScormTime(lessonId);
  };

  const flushAllScormTime = async () => {
    await Promise.all(
      Object.keys(scormTimeTrackersRef.current).map((lessonId) =>
        flushScormTime(Number(lessonId))
      )
    );
  };

  // A SCO reporting a final/passing status means the learner is done with
  // this course's content. Set course progress to 100% directly instead of
  // only relying on the lesson-count based aggregation (which can lag or
  // never reach 1 for SCORM courses, e.g. when progressLessonsTotal doesn't
  // line up with the SCOs the package actually reports on).
  const completeLessonAndProgress = async () => {
    await upsertLessonstatus({ status: 'completed' });
    if (isPreview || isTeacher) return;
    await updateFollow({ progress: 1 });
  };

  // ------------------------------------------------------------
  // init guard
  // ------------------------------------------------------------
  useEffect(() => {
    if (!course || !student || !iFrameRef.current || !activeNode || initialised)
      return;
    setInitialised(true);
  }, [course, student, activeNode, initialised]);

  // ------------------------------------------------------------
  // SCORM wiring
  // ------------------------------------------------------------
  useEffect(() => {
    if (!initialised) return;

    hideScroll();

    const subscriptions: any[] = [];
    subscriptionsRef.current = subscriptions;

    const scormData: any = {};

    const buildScormData = async () => {
      await ensureActiveLessonAndStatus();

      const activeStatus =
        activeStatusRef.current ||
        (activeLessonRef.current?.id
          ? findStatusRowByLessonId(activeLessonRef.current.id)
          : null);

      try {
        if (activeStatus?.status === 'completed')
          scormData['cmi.completion_status'] = 'completed';
        else if (!activeStatus)
          scormData['cmi.completion_status'] = 'not attempted';
        else scormData['cmi.completion_status'] = 'incomplete';
      } catch {
        scormData['cmi.completion_status'] = 'unknown';
      }

      try {
        if (isPreview) scormData['cmi.mode'] = 'browse';
        else if (activeStatus?.status === 'completed')
          scormData['cmi.mode'] = 'review';
        else scormData['cmi.mode'] = 'normal';
      } catch {
        scormData['cmi.mode'] = 'normal';
      }

      if (activeStatus?.location)
        scormData['cmi.location'] = activeStatus.location;
      if (activeStatus?.suspend_data)
        scormData['cmi.suspend_data'] = activeStatus.suspend_data;
    };

    subscriptions.push(
      ScormService.getInstance().Ready.subscribe(async () => {
        const win = iFrameRef.current?.contentWindow;
        if (!win) return;

        await buildScormData();
        win.postMessage({ type: 'data', data: scormData }, '*');
      })
    );

    subscriptions.push(
      ScormService.getInstance().Commit.subscribe(async () => {})
    );

    subscriptions.push(
      ScormService.getInstance().Terminate.subscribe(async () => {
        await flushAllScormTime();
        for (const s of subscriptions) s.unsubscribe();
        setNavigateTo(returnPath);
      })
    );

    subscriptions.push(
      ScormService.getInstance().SetValue.subscribe(async (temp: any) => {
        if (isPreview || isTeacher) return;

        if (!startDateSetRef.current) {
          const follow = studentRef.current;
          if (follow && !follow.startDate) {
            startDateSetRef.current = true;
            await updateFollow({ startDate: new Date() });
          } else {
            startDateSetRef.current = true;
          }
        }

        const [path, ...data] = temp;

        if (path.startsWith('cmi.interactions.')) {
          parseInteraction(path, data);
          return;
        }

        if (path === 'cmi.success_status') {
          await completeLessonAndProgress();
          return;
        }

        if (path === 'cmi.location') {
          await upsertLessonstatus({ location: data[0] });
          return;
        }

        if (path === 'cmi.suspend_data') {
          await upsertLessonstatus({ suspend_data: data[0] });
          return;
        }

        if (path === 'cmi.session_time' || path === 'cmi.core.session_time') {
          await persistScormSessionTime(data[0]);
          return;
        }

        if (path === 'cmi.score.raw') {
          const raw = parseFloat(data[0]);
          if (!Number.isNaN(raw)) await upsertLessonstatus({ score: raw });
          return;
        }

        if (path === 'cmi.score.max') {
          const mx = parseFloat(data[0]);
          if (!Number.isNaN(mx)) await upsertLessonstatus({ maxScore: mx });
          return;
        }

        if (path === 'cmi.score.scaled') {
          const scaled = parseFloat(data[0]);
          if (!Number.isNaN(scaled)) {
            const st = activeStatusRef.current;
            if (st?.maxScore && st.maxScore > 0) {
              await upsertLessonstatus({ score: scaled * st.maxScore });
            } else {
              await upsertLessonstatus({ score: scaled * 20, maxScore: 20 });
            }
          }
          return;
        }

        // SCORM 1.2 score paths
        if (path === 'cmi.core.score.raw') {
          const raw = parseFloat(data[0]);
          if (!Number.isNaN(raw)) await upsertLessonstatus({ score: raw });
          return;
        }
        if (path === 'cmi.core.score.max') {
          const mx = parseFloat(data[0]);
          if (!Number.isNaN(mx)) await upsertLessonstatus({ maxScore: mx });
          return;
        }

        if (path === 'cmi.completion_status') {
          if (data[0] === 'completed') await completeLessonAndProgress();
          else if (data[0] === 'incomplete')
            await upsertLessonstatus({ status: 'started' });
          return;
        }

        if (path === 'cmi.core.lesson_status') {
          if (data[0] === 'completed' || data[0] === 'passed') {
            await completeLessonAndProgress();
          } else if (data[0] === 'incomplete') {
            await upsertLessonstatus({ status: 'started' });
          }
          return;
        }

        console.error("I can't deal with", temp);
      })
    );

    return () => {
      showScroll();
      for (const s of subscriptions) s.unsubscribe();
    };
  }, [
    initialised,
    activeNode,
    statuses,
    numLesAs,
    isPreview,
    isTeacher,
    returnPath,
  ]);

  if (!course) return null;

  return (
    <div>
      {(hideSidebar || !isMobile) && (
        <ScormLessonHeader
          returnPath={returnPath}
          student={student}
          isPreview={isPreview}
          isMobile={isMobile}
          iSsmallLaptop={iSsmallLaptop}
          courseId={courseId}
          hideSidebar={hideSidebar}
          setHideSidebar={setHideSidebar}
        />
      )}

      <div className="flex">
        {!hideSidebar && (
          <div className="left-0 flex w-full flex-col border-r border-solid border-grey-02 p-5 px-2 duration-500 ease-out md:!max-w-[380px]">
            <div className="p-3 pt-0 sm:p-5 sm:pt-0 md:sticky md:top-[100px] md:max-h-[calc(100vh_-_100px)] md:w-[380px] md:overflow-y-auto">
              <div className={`flex w-full items-center ${isMobile && ''}`}>
                <p
                  className={`mt-2 mb-4 pl-2 text-base font-medium ${
                    isMobile && 'mx-auto mb-6'
                  }`}
                >
                  {t('pages.course_landing.contents')}
                </p>
                {isMobile && (
                  <IconButton
                    Icon={Cross}
                    variant="ghost"
                    className="mb-4 -mr-1 p-2.5"
                    onClick={() => setHideSidebar(!hideSidebar)}
                  />
                )}
              </div>

              <SidebarScormStudent
                key={course?.id + '_' + course?.publicationPath}
                coursecontentId={course.masterId || course.coursecontentId}
                publicationType={course.publicationType}
                publicationPath={course.publicationPath}
                activeNode={activeNode}
                autoInit={true}
                setNumLesAs={setNumLesAs}
                setActiveNode={realSetActiveNode}
                isDesctop={isDesctop}
                isMobile={isMobile}
                setHideSidebar={setHideSidebar}
                courseId={courseId}
                userId={userId}
                refetchNonce={refetchNonce}
              />
            </div>
          </div>
        )}

        <div>
          {course && activeNode && (
            <ScormLessonContent
              notHidden={!isMobile || hideSidebar}
              iFrameRef={iFrameRef}
              coursecontentId={course.masterId || course.coursecontentId}
              publicationPath={course.publicationPath}
              autoInit={true}
              node={activeNode}
              itemId={activeNode['@_identifier']}
              width={hideSidebar ? '100vw' : 'calc(100vw - 380px)'}
              setHideSidebar={setHideSidebar}
            />
          )}
        </div>
      </div>

      {numLesAs != 1 && (
        <div data-testid="hide-sidebar" className="hidden md:block">
          <IconButton
            className="fixed left-2 bottom-[90px] z-[4] bg-white hover:bg-[#f4f4f4]"
            Icon={Sidebar}
            variant="outline"
            onClick={() => setHideSidebar(!hideSidebar)}
          />
        </div>
      )}
    </div>
  );
};

export default ScormLessonPage;
